import { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Form } from 'react-bootstrap';
import { formatDate } from '../utils/dateFormat';

const money = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * On the GRN form: the vendor's bills recorded before their goods came. Goods for one of them must be received against
 * it (stock comes in; the vendor is already owed), so a received GRN for that vendor names the bill or says it's for
 * none of them. value holds { againstInvoiceId, billStillToArrive, notForOpenBills } as the GRN form keeps them.
 */
function GrnBillChoice({ vendorIds, value, onChange, onUseBillGoods, idPrefix = 'grn-bill' }) {
  const [bills, setBills] = useState([]);
  const [loadedKey, setLoadedKey] = useState(null);
  const key = [...new Set(vendorIds.filter(Boolean).map(String))].sort().join(',');

  useEffect(() => {
    if (!key) { setBills([]); setLoadedKey(''); return undefined; }
    let live = true;
    Promise.all(key.split(',').map((vendorId) => axios.get(`/api/purchase-invoices/vendor/${vendorId}/awaiting-goods`)
      .then((r) => (Array.isArray(r.data) ? r.data.map((b) => ({ ...b, vendorId: Number(vendorId) })) : []))
      .catch(() => [])))
      .then((lists) => { if (live) { setBills(lists.flat()); setLoadedKey(key); } });
    return () => { live = false; };
  }, [key]);

  // Once the bills for the current vendors are in: an answer about bills no longer offered (lines or vendor changed) goes
  const stale = loadedKey === key && (
    (value.againstInvoiceId && !bills.some((b) => String(b.id) === String(value.againstInvoiceId)))
    || (value.notForOpenBills && bills.length === 0));
  useEffect(() => {
    if (stale) onChange({ againstInvoiceId: '', billStillToArrive: false, notForOpenBills: false });
  }, [stale, onChange]);

  if (bills.length === 0) return null;
  const chosen = bills.find((b) => String(b.id) === String(value.againstInvoiceId));
  const selectValue = chosen ? String(chosen.id) : (value.notForOpenBills ? 'NONE' : '');

  return (
    <Alert variant="warning" className="small py-2 mb-3">
      <div className="mb-2">
        <strong>This vendor has bills with goods still to arrive.</strong> If these goods are for one of them, receive them
        against it: they go into stock, and the vendor isn&apos;t owed for them again.
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span>These goods are for</span>
        <Form.Select size="sm" style={{ width: 'auto', minWidth: '18rem' }} value={selectValue} aria-label="Bill these goods are for"
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'NONE') onChange({ againstInvoiceId: '', billStillToArrive: false, notForOpenBills: true });
            else onChange({ againstInvoiceId: v ? Number(v) : '', billStillToArrive: false, notForOpenBills: false });
          }}>
          <option value="">Choose...</option>
          {bills.map((b) => (
            <option key={b.id} value={b.id}>
              {b.internalNumber}{b.vendorInvoiceNumber ? ` (vendor's no. ${b.vendorInvoiceNumber})` : ''}, {formatDate(b.invoiceDate)}
            </option>
          ))}
          <option value="NONE">None of these bills</option>
        </Form.Select>
        {chosen && onUseBillGoods && (chosen.goods || []).length > 0 && (
          <Button size="sm" variant="outline-secondary" onClick={() => onUseBillGoods(chosen)}>Fill the lines from this bill</Button>
        )}
      </div>
      {chosen && (
        <>
          <div className="mt-2 text-muted">
            Billed: {(chosen.goods || []).map((g) => `${g.description}${g.quantity != null ? ` ${g.quantity} ${g.unitSymbol || ''}` : ''} (${money(g.amount)})`).join('; ') || 'no goods lines'}
          </div>
          <Form.Check className="mt-1" type="checkbox" id={`${idPrefix}-more`} checked={Boolean(value.billStillToArrive)}
            onChange={(e) => onChange({ ...value, billStillToArrive: e.target.checked })}
            label="More of this bill's goods are still to come (keep it open for another GRN)" />
        </>
      )}
      {value.notForOpenBills && (
        <div className="mt-2">These goods will be put on the vendor&apos;s account as usual, to be billed separately.</div>
      )}
    </Alert>
  );
}

export const emptyGrnBill = { againstInvoiceId: '', billStillToArrive: false, notForOpenBills: false };

export default GrnBillChoice;
